import { useAppSelector } from "../../hooks/redux";
import { useAppNavigate } from "../../hooks/router";
import { useEffect } from "react";
import { routes } from "../../router/public/routes";
import { IlocalStorageProvider } from "../../hexagon/adapters/driven/providers/localStorageProvider";
import { useLocation, useSearchParams } from "react-router-dom";
import { OnboardingPageStep } from "../../constants/getStarted";

interface IUseHook {
  localStorageProvider: IlocalStorageProvider;
  isPIN: () => boolean;
}

export const useHook = ({ localStorageProvider, isPIN }: IUseHook) => {
  const navigate = useAppNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const userName = localStorageProvider.get("username");
  const isCredentials = localStorageProvider.get("internalUse");
  const { isAuthorized, isPinAuthorized } = useAppSelector((state) => ({
    isPinAuthorized: state.auth.isPinAuthorized,
    isAuthorized: state.auth.isAuthorized,
  }));

  // Check if current path matches any public route
  const isPublicRoute = Object.values(routes).some(
    (route) =>
      location.pathname === route.path ||
      location.pathname.includes("public") ||
      // Handle dynamic routes with parameters
      (route.path.includes(":") &&
        new RegExp("^" + route.path.replace(/:[^/]+/g, "[^/]+") + "$").test(
          location.pathname
        ))
  );

  // Helper function to preserve zpax parameter in URLs
  const preserveZpaxParam = (url: string) => {
    const zpaxParam = searchParams.get("zpax");
    if (zpaxParam === "1") {
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}zpax=1`;
    }
    return url;
  };

  useEffect(() => {
    const isNewPassword = localStorageProvider.get("isNewPassword");
    if (
      isAuthorized &&
      (isPIN() ? !isPinAuthorized : false) &&
      (isPIN() ? isCredentials : false) &&
      !isNewPassword
    ) {
      navigate(preserveZpaxParam(routes.pinForm.path));
    }

    if (!isAuthorized && !isPublicRoute) {
      if (!localStorageProvider.get("isGetStartedVisited")) {
        const getStartedUrl = `${routes.getStarted.path}?step=${OnboardingPageStep.START}`;
        navigate(preserveZpaxParam(getStartedUrl));
        return;
      }
      navigate(preserveZpaxParam(routes.login.path));
    }
    // Do not add navigate in useEffect deps
  }, [isAuthorized, isCredentials, isPinAuthorized, userName]);

  return { uniquePageKey: location.key };
};
